/**
 * CollisionDebugPanel
 * Floating debug panel for visualizing collision detection state
 * Only shown when SHOW_DEBUG_PANEL is true (development mode)
 */

import { useState } from 'react'
import { useCollisionStore } from '@/stores/collisionStore'
import { SHOW_DEBUG_PANEL, ENABLE_COLLISION_DEBUG } from '@/constants/collision'

interface CollisionDebugPanelProps {
  /** Number of aircraft in the scene */
  aircraftCount: number
  /** Number of hangars configured for collision (legacy) */
  hangarCount: number
  /** Number of units with collision configs */
  unitCount?: number
}

export function CollisionDebugPanel({ aircraftCount,  unitCount = 0 }: CollisionDebugPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  const {
    collidingAircraftIds,
    collisionDetails,
    lastCheckDuration,
    isEnabled,
    isChecking,
  } = useCollisionStore()

  // Panel is intentionally force-disabled (never rendered); kept here for future re-enablement.
  // `panelDisabled` is a variable (not a literal) so the early return is not flagged as a
  // constant condition while still removing the component from the tree unconditionally.
  const panelDisabled = true
  if (!SHOW_DEBUG_PANEL || panelDisabled) return null

  const collidingArray = Array.from(collidingAircraftIds)

  return (
    <div className="fixed bottom-4 right-4 z-50 font-mono text-xs">
      {/* Toggle button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="mb-2 px-3 py-1 bg-gray-800 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
      >
        <span className={`w-2 h-2 rounded-full ${isEnabled ? 'bg-green-500' : 'bg-red-500'}`} />
        Collision Debug
        <span className="text-gray-400">{isExpanded ? '▼' : '▲'}</span>
      </button>

      {isExpanded && (
        <div className="bg-gray-900 text-white rounded-lg shadow-xl border border-gray-700 p-3 w-80">
          {/* Status */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-700">
            <span className="text-gray-400">Status</span>
            <span className={`px-2 py-0.5 rounded text-xs ${isEnabled ? 'bg-green-600' : 'bg-red-600'}`}>
              {isEnabled ? 'ENABLED' : 'DISABLED'}
            </span>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-gray-800 rounded p-2">
              <div className="text-gray-400 text-[10px] uppercase">Aircraft</div>
              <div className="text-lg font-bold">{aircraftCount}</div>
            </div>
            <div className="bg-gray-800 rounded p-2">
              <div className="text-gray-400 text-[10px] uppercase">Units</div>
              <div className="text-lg font-bold">{unitCount}</div>
            </div>
            <div className="bg-gray-800 rounded p-2">
              <div className="text-gray-400 text-[10px] uppercase">Colliding</div>
              <div className={`text-lg font-bold ${collidingArray.length > 0 ? 'text-red-500' : 'text-green-500'}`}>
                {collidingArray.length}
              </div>
            </div>
            <div className="bg-gray-800 rounded p-2">
              <div className="text-gray-400 text-[10px] uppercase">Last Check</div>
              <div className="text-lg font-bold">
                {lastCheckDuration.toFixed(1)}
                <span className="text-xs text-gray-400">ms</span>
              </div>
            </div>
          </div>

          {/* Checking indicator */}
          {isChecking && (
            <div className="text-yellow-500 text-xs mb-2 animate-pulse">
              ⏳ Checking...
            </div>
          )}

          {/* Colliding Aircraft List */}
          {collidingArray.length > 0 && (
            <div className="mb-3">
              <div className="text-gray-400 text-[10px] uppercase mb-1">Colliding Aircraft</div>
              <div className="bg-red-900/30 border border-red-800 rounded p-2 max-h-32 overflow-y-auto">
                {collidingArray.map((id) => {
                  const details = collisionDetails.get(id)
                  return (
                    <div key={id} className="flex items-center justify-between py-1 border-b border-red-800/50 last:border-0">
                      <span className="text-red-300 truncate" title={id}>
                        {id.slice(0, 12)}...
                      </span>
                      <span className="text-red-400 text-[10px]">
                        {details?.collisionType || 'unknown'}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Debug flags */}
          <div className="text-gray-500 text-[10px]">
            Debug: {ENABLE_COLLISION_DEBUG ? '✓' : '✗'} | Panel: {SHOW_DEBUG_PANEL ? '✓' : '✗'}
          </div>
        </div>
      )}
    </div>
  )
}
